import React, { useState, useEffect, useRef, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faUser, faEnvelope, faPhone, faBriefcase, faCalendarAlt,
  faIdCard, faDollarSign, faMapMarkerAlt, faUsers,
  faFileContract, faBuilding, faUserCheck, faSyncAlt,
  faExclamationTriangle, faChevronLeft, faChevronRight
} from '@fortawesome/free-solid-svg-icons';
import Dexie from 'dexie';
import API_CONFIG2 from '../../API_CONFIG/API_CONFIG2';

// ============================================================
// CACHE DEXIE (24h)
// ============================================================
const db = new Dexie('EmployeeProfileCacheDB');
db.version(1).stores({ profile: 'key, data, timestamp' });
const CACHE_DURATION = 24 * 60 * 60 * 1000;

async function getCachedProfile(key, allowExpired = false) {
  try {
    const entry = await db.profile.get(key);
    if (!entry) return null;
    const age = Date.now() - entry.timestamp;
    if (!allowExpired && age > CACHE_DURATION) return null;
    return entry.data;
  } catch { return null; }
}
async function setCachedProfile(key, data) {
  await db.profile.put({ key, data, timestamp: Date.now() });
}
async function getStaleProfile(key) {
  return getCachedProfile(key, true);
}

const API_URL = `${API_CONFIG2.apiUrl}employe/api_employe_profile.php`;

// ============================================================
// HOOK PERSONNALISÉ
// ============================================================
const useEmployeeProfile = (id) => {
  const [employee, setEmployee] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [source, setSource] = useState('cache');
  const abortControllerRef = useRef(null);
  const isMounted = useRef(true);

  const fetchProfile = useCallback(async (forceRefresh = false, retryCount = 0) => {
    if (!id) return;

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    const controller = new AbortController();
    abortControllerRef.current = controller;

    const cacheKey = `profile_${id}`;

    if (forceRefresh) setRefreshing(true);
    else setLoading(true);
    setError(null);

    try {
      // 1. Cache (sauf forceRefresh)
      if (!forceRefresh) {
        const cached = await getCachedProfile(cacheKey);
        if (cached && isMounted.current) {
          setEmployee(cached.employe);
          setSource('cache');
          setLoading(false);
          setRefreshing(false);
          return cached;
        }
      }

      // 2. Réseau
      const url = `${API_URL}?id=${id}`;
      const response = await fetch(url, { signal: controller.signal });

      if (response.status === 429) {
        const stale = await getStaleProfile(cacheKey);
        if (stale && isMounted.current) {
          setEmployee(stale.employe);
          setSource('cache (périmé)');
          setLoading(false);
          setRefreshing(false);
          return stale;
        }
        const waitTime = Math.min(1000 * Math.pow(2, retryCount), 30000);
        await new Promise(resolve => setTimeout(resolve, waitTime));
        return fetchProfile(forceRefresh, retryCount + 1);
      }

      if (!response.ok) {
        const stale = await getStaleProfile(cacheKey);
        if (stale && isMounted.current) {
          setEmployee(stale.employe);
          setSource('cache (périmé)');
          setLoading(false);
          setRefreshing(false);
          return stale;
        }
        throw new Error(`HTTP ${response.status}`);
      }

      const data = await response.json();
      if (data.status === 'success') {
        const result = { employe: data.employe };
        if (isMounted.current) {
          setEmployee(data.employe);
          const sourceLabel = forceRefresh ? 'live (forcé)' : 'live';
          setSource(sourceLabel);
          setLoading(false);
          setRefreshing(false);
          await setCachedProfile(cacheKey, result);
        }
        return result;
      } else {
        throw new Error(data.message || 'Erreur serveur');
      }
    } catch (err) {
      if (err.name === 'AbortError') return null;
      const stale = await getStaleProfile(cacheKey);
      if (stale && isMounted.current) {
        setEmployee(stale.employe);
        setSource('cache (périmé)');
        setLoading(false);
        setRefreshing(false);
        setError('Utilisation du cache périmé (erreur)');
        return stale;
      }
      if (isMounted.current) {
        setError(err.message || 'Erreur de chargement');
        setLoading(false);
        setRefreshing(false);
      }
      throw err;
    }
  }, [id]);

  useEffect(() => {
    isMounted.current = true;
    fetchProfile();
    return () => {
      isMounted.current = false;
      if (abortControllerRef.current) abortControllerRef.current.abort();
    };
  }, [fetchProfile]);

  const refresh = useCallback(() => {
    if (!loading && !refreshing) fetchProfile(true);
  }, [fetchProfile, loading, refreshing]);

  return { employee, loading, refreshing, error, source, refresh };
};

// ============================================================
// COMPOSANT PRINCIPAL
// ============================================================
const EmployeeProfile3 = ({ id }) => {
  const { employee, loading, refreshing, error, source, refresh } = useEmployeeProfile(id);

  const getPhotoUrl = (photoPath) => {
    if (!photoPath) return null;
    if (photoPath.startsWith('http://') || photoPath.startsWith('https://')) return photoPath;
    const cleanPath = photoPath.startsWith('/') ? photoPath.slice(1) : photoPath;
    return `${API_CONFIG2.apiUrl}/${cleanPath}`;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('fr-FR');
  };

  if (loading) {
    return (
      <div className="profile-loading">
        <div className="spinner"></div>
        <p>Chargement du profil...</p>
      </div>
    );
  }

  if (error && !employee) {
    return (
      <div className="profile-error">
        <FontAwesomeIcon icon={faExclamationTriangle} />
        <p>{error}</p>
        <button onClick={refresh} className="btn-retry">
          <FontAwesomeIcon icon={faSyncAlt} /> Réessayer
        </button>
      </div>
    );
  }

  if (!employee) return null;

  const statusLabel = employee.est_actif == 1 ? 'Actif' : 'Inactif';

  return (
    <>
      <style>{`
        :root {
          --bg-primary: #0b1428;
          --card-bg: rgba(255,255,255,0.04);
          --card-border: rgba(255,255,255,0.06);
          --text-primary: #e0e7ff;
          --text-secondary: #94a3b8;
          --accent-blue: #3b82f6;
          --radius: 20px;
          --shadow: 0 8px 30px rgba(0,0,0,0.3);
        }

        .profile-container {
          background-color: var(--bg-primary);
          min-height: 100vh;
          padding: 1.5rem;
          color: var(--text-primary);
          font-family: 'Inter', -apple-system, sans-serif;
        }

        .profile-inner {
          max-width: 1000px;
          margin: 0 auto;
        }

        .card-glass {
          background: var(--card-bg);
          backdrop-filter: blur(12px);
          border: 1px solid var(--card-border);
          border-radius: var(--radius);
          padding: 1.5rem;
          box-shadow: var(--shadow);
          margin-bottom: 1.5rem;
          transition: transform 0.2s;
        }

        .card-glass:hover {
          transform: translateY(-2px);
        }

        /* Header */
        .profile-header {
          display: flex;
          align-items: center;
          gap: 2rem;
          flex-wrap: wrap;
        }

        .avatar {
          width: 100px;
          height: 100px;
          border-radius: 50%;
          overflow: hidden;
          border: 3px solid rgba(59,130,246,0.3);
          flex-shrink: 0;
          background: rgba(255,255,255,0.05);
        }
        .avatar img {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }
        .avatar .placeholder {
          width: 100%;
          height: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 3rem;
          color: rgba(255,255,255,0.2);
        }

        .profile-title h2 {
          margin: 0;
          font-size: 1.8rem;
          font-weight: 700;
        }
        .profile-title .sub {
          color: var(--text-secondary);
          display: flex;
          flex-wrap: wrap;
          gap: 1rem;
          margin-top: 0.25rem;
          font-size: 0.95rem;
        }
        .badge-status {
          display: inline-block;
          padding: 0.2rem 1rem;
          border-radius: 30px;
          font-size: 0.7rem;
          font-weight: 600;
          text-transform: uppercase;
          background: rgba(59,130,246,0.15);
          color: var(--accent-blue);
          border: 1px solid rgba(59,130,246,0.2);
        }
        .badge-status.inactif {
          background: rgba(239,68,68,0.15);
          color: #ef4444;
          border-color: rgba(239,68,68,0.2);
        }

        .source-badge {
          font-size: 0.7rem;
          background: rgba(59,130,246,0.12);
          padding: 0.2rem 0.8rem;
          border-radius: 30px;
          color: var(--accent-blue);
          border: 1px solid rgba(59,130,246,0.1);
        }

        .btn-refresh {
          background: transparent;
          border: 1px solid rgba(255,255,255,0.1);
          color: var(--text-primary);
          padding: 0.4rem 1.2rem;
          border-radius: 30px;
          cursor: pointer;
          transition: 0.2s;
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
        }
        .btn-refresh:hover:not(:disabled) {
          background: rgba(255,255,255,0.05);
        }
        .btn-refresh:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        /* Grid */
        .info-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
          gap: 1.2rem;
          margin-top: 1rem;
        }

        .info-item {
          background: rgba(255,255,255,0.02);
          border-radius: 12px;
          padding: 0.6rem 1rem;
          border: 1px solid rgba(255,255,255,0.03);
        }
        .info-item .label {
          font-size: 0.65rem;
          text-transform: uppercase;
          color: var(--text-secondary);
          opacity: 0.7;
          letter-spacing: 0.3px;
        }
        .info-item .value {
          font-size: 0.95rem;
          font-weight: 500;
          margin-top: 0.2rem;
          word-break: break-word;
        }

        .section-title {
          font-size: 1.1rem;
          font-weight: 600;
          display: flex;
          align-items: center;
          gap: 0.6rem;
          border-left: 3px solid var(--accent-blue);
          padding-left: 0.8rem;
          margin-bottom: 1rem;
        }

        /* Loader & Error */
        .profile-loading, .profile-error {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          height: 300px;
          gap: 1rem;
        }
        .spinner {
          width: 40px;
          height: 40px;
          border: 4px solid rgba(59,130,246,0.1);
          border-top-color: var(--accent-blue);
          border-radius: 50%;
          animation: spin 0.8s linear infinite;
        }
        @keyframes spin { to { transform: rotate(360deg); } }

        .profile-error {
          background: var(--card-bg);
          border-radius: var(--radius);
          padding: 2rem;
          max-width: 500px;
          margin: 2rem auto;
        }
        .btn-retry {
          background: var(--accent-blue);
          border: none;
          color: white;
          padding: 0.5rem 1.5rem;
          border-radius: 30px;
          cursor: pointer;
        }

        /* Responsive */
        @media (max-width: 768px) {
          .profile-container { padding: 1rem; }
          .profile-header { flex-direction: column; text-align: center; }
          .profile-title .sub { justify-content: center; }
          .info-grid { grid-template-columns: 1fr 1fr; }
        }
        @media (max-width: 480px) {
          .info-grid { grid-template-columns: 1fr; }
        }
      `}</style>

      <div className="profile-container">
        <div className="profile-inner">
          {/* Header avec source et refresh */}
          <div className="card-glass">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', marginBottom: '1rem' }}>
              <span className="source-badge">
                {source === 'cache' ? '📦 Cache' :
                 source === 'cache (périmé)' ? '⚠️ Cache périmé' :
                 source === 'live' ? '🔄 Live' :
                 source === 'live (forcé)' ? '🔄 Forcé' : '📦 Cache'}
              </span>
              <button className="btn-refresh" onClick={refresh} disabled={loading || refreshing}>
                <FontAwesomeIcon icon={faSyncAlt} spin={loading || refreshing} />
                {refreshing ? 'Actualisation...' : 'Actualiser'}
              </button>
            </div>

            <div className="profile-header">
              <div className="avatar">
                {employee.photo ? (
                  <img src={getPhotoUrl(employee.photo)} alt={`${employee.prenom} ${employee.nom}`} />
                ) : (
                  <div className="placeholder"><FontAwesomeIcon icon={faUser} /></div>
                )}
              </div>
              <div className="profile-title">
                <h2>{employee.prenom} {employee.nom}</h2>
                <div className="sub">
                  <span><FontAwesomeIcon icon={faIdCard} /> {employee.matricule}</span>
                  <span><FontAwesomeIcon icon={faBriefcase} /> {employee.fonction || employee.poste || 'Non défini'}</span>
                  <span><FontAwesomeIcon icon={faBuilding} /> {employee.service}</span>
                  <span><FontAwesomeIcon icon={faCalendarAlt} /> Embauche : {formatDate(employee.date_embauche)}</span>
                  <span className={`badge-status ${employee.est_actif == 1 ? '' : 'inactif'}`}>{statusLabel}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Identité & contact */}
         

          {/* Adresse */}
      

          {/* Urgence */}
      

          {/* Emploi et contrat */}
         

          {/* Banque et numéros */}
      

          {/* Observation */}
          {employee.observation && (
            <div className="card-glass">
              <div className="section-title">📝 Observation</div>
              <p style={{ whiteSpace: 'pre-wrap', margin: 0 }}>{employee.observation}</p>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default EmployeeProfile3;